import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/lib/persona";
import { resolvePostAuthTarget, takePostAuthTarget } from "@/lib/postAuthRedirect";

const searchSchema = z.object({
  returnTo: z.string().optional(),
  email: z.preprocess((value) => value === "" ? undefined : value, z.string().email().optional()),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  component: LoginPage,
});

/**
 * Sanitize `returnTo` to prevent open-redirect attacks. Only same-origin
 * paths that start with `/` and NOT `//` (protocol-relative) are safe.
 * Anything else falls back to `/app`.
 */
function safeReturnTo(raw: string | undefined): string {
  if (!raw) return "/app";
  if (!raw.startsWith("/")) return "/app";
  if (raw.startsWith("//")) return "/app";
  return raw;
}

function LoginPage() {
  const persona = usePersona();
  const { user, loading, signIn } = useAuth();
  const { returnTo, email: emailFromSearch } = Route.useSearch();
  const navigate = useNavigate();

  const [email, setEmail] = useState(emailFromSearch ?? "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const emailSearch = email.trim() || undefined;

  useEffect(() => {
    if (loading) return;
    if (user && user.groups.includes(persona.role)) {
      window.location.assign(safeReturnTo(returnTo));
    }
  }, [loading, user, persona.role, returnTo]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setErrorCode(null);
    setSubmitting(true);
    try {
      await signIn({ email: email.trim().toLowerCase(), password });
      // Funnel policy: explicit saved target (came from /passes) wins, then
      // ?returnTo=, then buyers → /app and non-buyers → /passes (pricing is
      // the default landing until they own a pass).
      let hasPass = false;
      try {
        const pass = await api<{ has_active_pass: boolean }>("/api/v1/me/pass");
        hasPass = pass.has_active_pass;
      } catch { /* endpoint hiccup — fall through to /passes default */ }
      const target = resolvePostAuthTarget({
        savedTarget: takePostAuthTarget(),
        explicitReturnTo: returnTo ? safeReturnTo(returnTo) : null,
        hasActivePass: hasPass,
      });
      void navigate({ to: target as never, replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        const code = e.problem.type?.split("/").pop() ?? "auth_unknown";
        setErrorCode(code);
        setErrorMsg(e.problem.detail ?? "Sign-in failed. Please try again.");
      } else {
        setErrorMsg("Sign-in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <section className="container py-16 md:py-20">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle className="font-serif text-3xl text-palm">Sign in</CardTitle>
            <CardDescription>
              Continue to <span className="font-medium text-foreground">{persona.brand.name}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* First-time visitors from the purchase funnel need signup to be
                impossible to miss — most people landing here have no account. */}
            <div className="mb-5 rounded-lg border border-palm/25 bg-sand px-4 py-3 text-center">
              <p className="text-sm text-palm">
                <strong>First time here?</strong> Create your account in under a minute.
              </p>
              <Button asChild variant="outline" size="sm" className="mt-2 w-full border-palm/40">
                <Link to={"/signup" as never} search={{ email: emailSearch, returnTo } as never}>Create account →</Link>
              </Button>
            </div>
            <div className="mb-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              or sign in
              <span className="h-px flex-1 bg-border" />
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to={"/forgot-password" as never}
                    search={{ email: emailSearch } as never}
                    className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  >
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {errorMsg && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMsg}
                  {errorCode === "auth_not_confirmed" && (
                    <div className="mt-2">
                      <Link
                        to={"/confirm" as never}
                        search={{ email, returnTo } as never}
                        className="text-xs underline"
                      >
                        Confirm your email now →
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" variant="accent" disabled={submitting || loading}>
                {submitting ? "Signing in…" : "Sign in"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                No account?{" "}
                <Link to={"/signup" as never} search={{ email: emailSearch, returnTo } as never}
                  className="text-palm underline underline-offset-2 hover:text-accent">
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
